import { getCurrentTimeOffset } from "./dateHelpers";
import type {
  BillingInfo,
  FileParameter,
  AllocationManagement,
  ActivateRequest,
  SetDefaultCardRequest,
  SaveBankAccountRequest,
  SetDefaultBankAccountRequest,
  UpdateNotificationContactRequest,
  AccountInfo,
  SetupStartRequest,
  SetupFinalizeRequest,
  CustomerInfo,
  LicenseRequestType,
  AddUsersToGroupsRequest,
  SaveIndividualAllocationRequest,
  UsageLogRequest,
  UsageHistoryRequest,
  RequestUpgradeSubscriptionRequest,
  QueueReleaseLicenseLeasesRequest,
  CapacityReportRequest,
} from "@/api-clients/dashboard-api";
import { callDashboardApi } from "./apiDashboardHelpers";

export {
  ApiException,
  CardType,
  TaxBlockType,
  PaymentMethodType,
  LicenseRequestType,
  UsageLogSortField,
  UsageHistoryDateGroupEnum,
  UsageHistoryGroupField,
  UsageHistorySortField,
  SubscriptionState,
  SubscriptionPaymentTerm,
  DashboardPermission
} from "@/api-clients/dashboard-api";

export type {
  UserAuthorization,
  ListOfLicenses,
  License,
  LicenseGroup,
  UsersAssignedCount,
  LicenseFull,
  LicenseDetails,
  LicenseFilterOptions,
  ListOfAllocations,
  AllocationManagement,
  AddSeatPreview,
  UpgradeGeoScopePreview,
  WorkspaceInfo,
  PaymentMethod as CardInfo,
  Invoice,
  BillingInfo,
  Address,
  Country,
  State,
  LayoutContent,
  UserInfo,
  SaveBankAccountRequest,
  BankAccountPublicLinkToken,
  CustomerInfo,
  LicenseRequestInfo,
  RealTimeUsageInfo,
  RealTimeUsageLicenseInfo,
  RealTimeUsageDetails,
  UsageLogRequest,
  UsageLogInfo,
  UsageHistoryRequest,
  UsageHistoryInfo,
  UsageHistoryItem,
  AddSeatNotification,
  LicenseShort,
  LicensesSummaryInfo,
  SubscriptionInfo
} from "@/api-clients/dashboard-api";

export async function getAccountInfo() {
  return await callDashboardApi(apiClient => apiClient.getAccountInfo());
}

export async function getUserAuthorization() {
  return await callDashboardApi(apiClient => apiClient.getUserAuthorization());
}

export async function getLicenses() {
  return await callDashboardApi(apiClient => apiClient.getLicenses());
}

export async function getArchivedLicenses() {
  return await callDashboardApi(apiClient => apiClient.getArchivedLicenses());
}

export async function getLicensesUsersAssigned(licenseIds: string[]) {
  return await callDashboardApi(apiClient => apiClient.getUsersAssignedCount(licenseIds));
}

export async function getLicenseInfo(licenseId: string) {
  return await callDashboardApi(apiClient => apiClient.getLicenseInfo(licenseId));
}

export async function getLicenseDetails(licenseId: string) {
  return await callDashboardApi(apiClient => apiClient.getLicenseDetails(licenseId));
}

export async function getLicenseFilterOptions() {
  return await callDashboardApi(apiClient => apiClient.getLicenseFilterOptions());
}

export async function getAddSeatInfo(licenseId: string) {
  return await callDashboardApi(apiClient => apiClient.getAddSeatInfo(licenseId));
}

export async function previewAddSeat(licenseId: string, seatCount: number) {
  return await callDashboardApi(apiClient => apiClient.previewAddSeat(licenseId, seatCount));
}

export async function previewUpgradeGeoScope(licenseId: string) {
  return await callDashboardApi(apiClient => apiClient.previewUpgradeGeoScope(licenseId));
}

export async function getActivationInfo(licenseId: string) {
  return await callDashboardApi(apiClient => apiClient.getActivationInfo(licenseId));
}

export async function getActivationUrl(licenseId: string, productId: number, isPrimary: boolean) {
  const arg: ActivateRequest = {
    licenseId: licenseId,
    productId: productId,
    isPrimary: isPrimary
  };
  return await callDashboardApi(apiClient => apiClient.getActivationUrl(arg));
}

export async function requestUpgradeSubscription(phone: string, email: string, accountId?: string) {
  const arg: RequestUpgradeSubscriptionRequest = {
    phone,
    email,
    accountId
  };
  return await callDashboardApi(apiClient => apiClient.requestUpgradeSubscription(arg));
}

export async function getAllocations(licenseId: string) {
  return await callDashboardApi(apiClient => apiClient.getAllocations(licenseId));
}

export async function getAllocationForCreate(licenseId: string) {
  return await callDashboardApi(apiClient => apiClient.getAllocationForCreate(licenseId));
}

export async function getAllocationForEdit(licenseId: string, allocationId: string) {
  return await callDashboardApi(apiClient => apiClient.getAllocationForEdit(licenseId, allocationId));
}

export async function saveAllocation(allocation: AllocationManagement) {
  return await callDashboardApi(apiClient => apiClient.saveAllocation(allocation));
}

export async function deleteAllocation(allocationId: string) {
  return await callDashboardApi(apiClient => apiClient.deleteAllocation(allocationId));
}

export async function getIndividualAllocation(licenseId: string) {
  return await callDashboardApi(apiClient => apiClient.getIndividualAllocation(licenseId));
}

export async function getUsersAssignableToIndividualLicense(licenseId: string) {
  return await callDashboardApi(apiClient => apiClient.getUsersAssignableToIndividualLicense(licenseId));
}

export async function saveIndividualAllocation(licenseId: string, licenseUserId: string) {
  const arg: SaveIndividualAllocationRequest = {
    licenseId,
    licenseUserId
  };
  return await callDashboardApi(apiClient => apiClient.saveIndividualAllocation(arg));
}

export async function getWorkspacesInfo() {
  return await callDashboardApi(apiClient => apiClient.getWorkspacesInfo());
}

export async function getInvoices() {
  return await callDashboardApi(apiClient => apiClient.getAllInvoicesForCustomer());
}

export async function getBillingInfo() {
  return await callDashboardApi(apiClient => apiClient.getBillingInfo());
}

export async function updateBillingInfo(billingInfo: BillingInfo) {
  return await callDashboardApi(apiClient => apiClient.updateBillingInfo(billingInfo));
}

export async function getStates(countryCode?: string) {
  return await callDashboardApi(apiClient => apiClient.getStates(countryCode));
}

export async function getLayoutContent(content: "header" | "footer") {
  return await callDashboardApi(apiClient => apiClient.getLayoutContent(content));
}

export async function uploadExemptionCertificate(fileName: string, fileContent: Blob) {
  const image: FileParameter = {
    fileName: fileName,
    data: fileContent
  };
  return await callDashboardApi(apiClient => apiClient.uploadExemptionCertificate(image));
}

export async function getStripePaymentMethods() {
  return await callDashboardApi(apiClient => apiClient.getCards());
}

export async function listBankAccounts() {
  return await callDashboardApi(client => client.listBankAccounts());
}

export async function finalizeStripe3DSCheck(paymentMethodId: string) {
  return await callDashboardApi(apiClient => apiClient.finalize3DSCheck(paymentMethodId));
}

export async function setDefaultStripePaymentMethod(paymentMethodId: string) {
  const arg: SetDefaultCardRequest = {
    creditCardId: paymentMethodId
  };
  return await callDashboardApi(apiClient => apiClient.setDefaultCard(arg));
}

export async function removeStripePaymentMethod(paymentMethodId: string) {
  return await callDashboardApi(apiClient => apiClient.removeCard(paymentMethodId));
}

export async function getSubscriptionUpgradeUrl() {
  return await callDashboardApi(apiClient => apiClient.getSubscriptionUpgradeUrl());
}

export async function getAccountAdmins() {
  return await callDashboardApi(client => client.getAccountAdmins());
}

export async function getAccountUsers() {
  return await callDashboardApi(client => client.getAccountUsers());
}

export async function getAccountGroups() {
  return await callDashboardApi(client => client.getAccountGroups());
}

export async function getAvailablePaymentMethods() {
  return await callDashboardApi(client => client.getAvailablePaymentMethods());
}

export async function getBankingAccountLinkToken() {
  return await callDashboardApi(client => client.getBankAccountLinkToken());
}

export async function saveBankAccount(request: SaveBankAccountRequest) {
  return await callDashboardApi(client => client.saveBankAccount(request));
}

export async function setDefaultBankAccount(accountId: string) {
  const arg: SetDefaultBankAccountRequest = {
    accountId
  };

  return await callDashboardApi(client => client.setDefaultBankAccount(arg));
}

export async function getUserLicenses() {
  return await callDashboardApi(client => client.getUserLicenses());
}

export async function updateNotificationContact(licenseId: string, contactId: string) {
  const arg: UpdateNotificationContactRequest = {
    licenseId,
    contactId
  };

  return await callDashboardApi(client => client.updateNotificationContact(arg));
}

export async function saveAccountInfo(info: AccountInfo) {
  return await callDashboardApi(client => client.saveAccountInfo(info));
}

export async function getSubscriptionLevel() {
  return await callDashboardApi(c => c.getUserSubscriptionLevel());
}

export async function updateAccountLogo(file: FileParameter) {
  return await callDashboardApi(client => client.updateAccountLogo(file));
}

export function removeAccountLogo() {
  return callDashboardApi(c => c.removeAccountLogo());
}

export async function startSetup(countryCode?: string) {
  const arg: SetupStartRequest = {
    countryCode
  };

  return await callDashboardApi(client => client.startSetup(arg));
}

export async function finalizeSetup(entityId: string, setupIntentId: string, newCustomer?: CustomerInfo) {
  const arg: SetupFinalizeRequest = {
    entityId,
    setupIntentId,
    isNewCustomer: newCustomer != null,
    newCustomer: newCustomer!
  };

  return await callDashboardApi(client => client.finalizeSetup(arg));
}

export async function getLicenseRequests() {
  return await callDashboardApi(c => c.getLicenseRequests());
}

export async function getRequestsForLicense(licenseId: string, type: LicenseRequestType) {
  return await callDashboardApi(c => c.getRequestsForLicense(licenseId, type));
}

export async function acknowledgeRequests(ids: string[]) {
  const request = {
    requests: ids
  };

  return await callDashboardApi(c => c.acknowledgeRequests(request));
}

export async function getLicenseAccessRequests() {
  return await callDashboardApi(c => c.getLicenseAccessRequests());
}

export async function getGroupsWithAllocations() {
  return await callDashboardApi(c => c.getGroupsWithAllocations());
}

export async function sendTradeUpNotification(licenseId: string) {
  return await callDashboardApi(c => c.sendTrade(licenseId));
}

export async function addUsersToGroups(usersGroups: Map<string, string>) {
  const map = Array
    .from(usersGroups.entries())
    .reduce((acc, [key, val]) => {
      acc[key] = val;

      return acc;
    }, {} as { [key: string]: string });

  const request: AddUsersToGroupsRequest = {
    usersGroupsMap: map
  }
  return await callDashboardApi(c => c.addUsersToGroups(request));
}

export async function realTimeUsageReportSummary() {
  return await callDashboardApi(client => client.realTimeUsageReportSummary());
}

export async function realTimeUsageReportDetails(licenseId: string) {
  return await callDashboardApi(client => client.realTimeUsageReportDetails(licenseId, getCurrentTimeOffset()));
}

export async function queueForceReleaseLicenseLeases(licenseId: string, leaseIds: string[]) {
  const request: QueueReleaseLicenseLeasesRequest = {
    leaseIds,
    timeOffsetInMinutes: getCurrentTimeOffset()
  };

  await callDashboardApi(client => client.queueForceReleaseLicenseLeases(licenseId, request));
}

export async function usageLogReport(request: UsageLogRequest) {
  return await callDashboardApi(client => client.usageLogReport(request));
}

export async function usageLogReportToCsv(request: UsageLogRequest) {
  return await callDashboardApi(client => client.usageLogReportToCsv(request));
}

export async function usageHistoryReport(request: UsageHistoryRequest) {
  return await callDashboardApi(client => client.usageHistoryReport(request));
}

export async function usageHistoryReportToCsv(request: UsageHistoryRequest) {
  return await callDashboardApi(client => client.usageHistoryReportToCsv(request));
}

export async function getAddSeatRequests() {
  return await callDashboardApi(c => c.getAddSeatRequests());
}

export async function getLicensesSummaryInfo() {
  return await callDashboardApi(c => c.getSummaryInfo());
}

export async function getIndustries() {
  return await callDashboardApi(c => c.getIndustries());
}

export async function realTimeCapacityReport(request: CapacityReportRequest) {
  return await callDashboardApi(c => c.realTimeCapacity(request));
}

export async function cancelSubscription(id: string) {
  return await callDashboardApi(apiClient => apiClient.cancelSubscription(id));
}
